/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import { MutableRef, useRef } from 'preact/hooks';
import { handleInput } from './events';
import { CommitDetail, InputDetail } from '#utils/UNSAFE_meterUtils';

type getValueFromEvent = (event: PointerEvent) => number | undefined;

export function usePointerEvents(
  value: number | undefined,
  getValue: getValueFromEvent,
  targetRef: RefObject<HTMLDivElement>,
  currentInputRef: MutableRef<number | undefined>,
  isPointerActiveRef: MutableRef<boolean | undefined>,
  onCommit?: (detail: CommitDetail) => void,
  onInput?: (detail: InputDetail) => void
) {
  const isPointerDown = useRef<boolean>(false);

  const onPointerUp = (event: PointerEvent) => {
    isPointerDown.current = false;
    const val = getValue(event);
    if (val !== undefined) {
      onCommit?.({ value: val } as CommitDetail);
    }
    isPointerActiveRef.current = true;
  };

  const onPointerMove = (event: PointerEvent) => {
    if (isPointerDown.current) {
      const val = getValue(event);
      if (val !== undefined && val != currentInputRef.current) {
        handleInput(val, currentInputRef, onInput);
      }
    }
    isPointerActiveRef.current = true;
  };

  const onPointerDown = (event: PointerEvent) => {
    isPointerDown.current = true;
    targetRef.current?.setPointerCapture(event.pointerId);
    const val = getValue(event);
    if (val !== undefined && val != currentInputRef.current) {
      handleInput(val, currentInputRef, onInput);
    }
    isPointerActiveRef.current = true;
  };

  const onBlur = () => {
    isPointerDown.current = false;
    onCommit?.({ value: value } as CommitDetail);
    isPointerActiveRef.current = false;
  };

  const onPointerLeave = () => {
    isPointerActiveRef.current = false;
  };

  const onPointerEnter = () => {
    isPointerActiveRef.current = true;
  };

  return {
    onPointerUp,
    onBlur,
    onPointerMove,
    onPointerDown,
    onPointerLeave,
    onPointerEnter
  };
}
